import React, { type ComponentType } from "react";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  size,
  useFloating,
  type Placement,
} from "@floating-ui/react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Menu } from "@headlessui/react";
import { Link } from "react-router-dom";
import { twJoin, twMerge } from "tailwind-merge";
import { ChevronIcon, ExclamationCircleIcon } from "@components/icons";
import { Tooltip } from "@components/Tooltip/Tooltip";
import { isNullOrUndefined, parseTitleAttribute } from "@utils/common";

type TValue = string | number;
type TLabel = string | number;

export type TDropdownOption<Label, Value> = {
  /**
   * Option label.
   */
  label: Label;
  /**
   * Option value.
   */
  value?: Value;
  /**
   * Option link if used as anchor.
   */
  href?: string;
  /**
   * Active option flag.
   */
  active?: boolean;
  /**
   * Disabled option flag.
   */
  disabled?: boolean;
  /**
   * Option icon element.
   */
  icon?: ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Option click callback.
   */
  onClick?: () => void;
  /**
   * Whether item should display exclamation icon.
   */
  isError?: boolean;
  /**
   * HTML title attribute.
   */
  htmlTitle?: string;
  /**
   * Option className.
   */
  className?: string;
  /**
   * Option icon className.
   */
  iconClassName?: string;
};

export interface IDropdownProps<Label, Value> {
  /**
   * Available options array.
   */
  options?: TDropdownOption<Label, Value>[];
  /**
   * Dropdown option value.
   */
  value?: Value;
  /**
   * Dropdown label.
   */
  label?: string;
  /**
   * Element label.
   */
  ariaLabel?: string;
  /**
   * Value change callback.
   */
  onChange?: (value: Value) => void;
  /**
   * Dropdown blur callback.
   */
  onBlur?: () => void;
  /**
   * Dropdown disabled flag.
   */
  disabled?: boolean;
  /**
   * Dropdown button ref.
   */
  buttonRef?: (instance: never) => void;
  /**
   * Dropdown icon element.
   */
  icon?: ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Button classname.
   */
  className?: string;
  /**
   * Dropdown container classname.
   */
  containerClassName?: string;
  /**
   * Hide chevron icon.
   */
  isChevronHidden?: boolean;
  /**
   * Dropdown placement.
   */
  placement?: Placement;
  /**
   * Custom button component callback.
   */
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  buttonAs?: (props) => JSX.Element;
  /**
   * Error text.
   */
  error?: string;
  /**
   * Whether dropdown content should stay in fixed position.
   */
  isPositionFixed?: boolean;
  /**
   * Minimum dropdown container width in pixels.
   */
  minContainerWidth?: number;
  /**
   * Whether options should be truncated.
   */
  shouldTruncateOptions?: boolean;
}

export const Dropdown = <Label = TLabel, Value = TValue>({
  options = [],
  label,
  ariaLabel,
  value,
  onChange,
  onBlur,
  disabled,
  buttonRef,
  icon: Icon,
  className,
  containerClassName,
  isChevronHidden,
  placement = "bottom-start",
  buttonAs,
  error,
  isPositionFixed,
  minContainerWidth = 244,
  shouldTruncateOptions = true,
}: IDropdownProps<Label, Value>) => {
  const [animationParent] = useAutoAnimate({ duration: 100 });

  const { refs, floatingStyles } = useFloating({
    placement,
    strategy: isPositionFixed ? "fixed" : "absolute",
    middleware: [
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            minWidth: `${minContainerWidth}px`,
          });
        },
      }),
      flip(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const selectedOption = !isNullOrUndefined(value)
    ? options.find((o) => o.value === value)
    : undefined;

  return (
    <Menu>
      {({ open }) => (
        <div className="h-full" ref={refs.setReference}>
          <div className="relative flex h-full" ref={animationParent}>
            {!isNullOrUndefined(buttonAs) ? (
              <Menu.Button as={buttonAs} aria-label={ariaLabel} />
            ) : (
              <Menu.Button
                name="name"
                aria-label={ariaLabel}
                ref={buttonRef}
                className={twMerge(
                  "relative h-10 w-full max-w-52 rounded-full border border-grey-hover bg-base-white pl-3 pr-3 text-left text-sm font-semibold leading-5 text-base-black focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-base-light-grey focus-visible:ring-opacity-100",
                  Icon && "pl-9",
                  !disabled && "hover:border-grey-dark",
                  open && "ring-4 ring-base-light-grey ring-opacity-100",
                  disabled && "border-grey-light text-grey-hover",
                  className,
                )}
                onBlur={onBlur}
                disabled={disabled}
              >
                {Icon && (
                  <Icon
                    className={twMerge(
                      "absolute inset-y-0 left-3 z-0 m-auto h-4 w-4 text-base-black",
                      disabled && "text-grey-hover",
                    )}
                  />
                )}
                <div className="flex items-center">
                  <span className="block truncate">
                    {!isNullOrUndefined(label)
                      ? label
                      : (selectedOption?.label as string)}
                  </span>
                  {(!isChevronHidden || !isNullOrUndefined(error)) && (
                    <div className="flex flex-grow items-center justify-end gap-1 pl-2">
                      {!isNullOrUndefined(error) && (
                        <Tooltip content={error}>
                          {(props) => (
                            <span
                              className={twJoin(
                                "text-red-dark",
                                disabled && "text-grey-hover",
                              )}
                              {...props}
                            >
                              <ExclamationCircleIcon className={"h-5 w-5"} />
                            </span>
                          )}
                        </Tooltip>
                      )}
                      {!isChevronHidden && (
                        <span className="pointer-events-none">
                          <ChevronIcon
                            className={twJoin(
                              "h-5 w-5 text-base-black transition-transform duration-100",
                              open && "rotate-180",
                              disabled && "text-grey-hover",
                            )}
                          />
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </Menu.Button>
            )}
            <FloatingPortal>
              <Menu.Items
                ref={refs.setFloating}
                style={floatingStyles}
                className={twMerge(
                  "pointer-events-auto absolute z-[9999] mt-2 flex max-h-64 w-full flex-col gap-1 overflow-auto rounded-xl border border-grey-light bg-base-white p-2 text-base text-base-black shadow-lg focus:outline-none",
                  containerClassName,
                )}
              >
                {options.map((option, optionIndex) => {
                  const isOptionActive =
                    option.active ??
                    (!isNullOrUndefined(value) && value === option.value);

                  return (
                    <Menu.Item
                      key={optionIndex}
                      disabled={option.disabled}
                      className={({ active }) =>
                        twMerge(
                          "relative flex select-none flex-row items-center rounded-full px-4 py-2 text-sm font-normal",
                          !option.disabled && "cursor-pointer",
                          isOptionActive &&
                            "bg-blue-light font-medium text-blue-dark",
                          active && "bg-base-light-grey",
                          option.disabled && "text-grey-hover",
                          option.icon && "gap-2",
                          option.isError && "pr-10",
                          !shouldTruncateOptions && "rounded-3xl",
                          option.className,
                        )
                      }
                      as={
                        option.disabled
                          ? "span"
                          : !isNullOrUndefined(option.href)
                            ? Link
                            : "a"
                      }
                      title={
                        option.htmlTitle ?? parseTitleAttribute(option.label)
                      }
                      {...(!option.disabled
                        ? {
                            onClick: () => {
                              option.onClick?.();

                              if (!isNullOrUndefined(option.value)) {
                                onChange?.(option.value);
                              }
                            },
                          }
                        : {})}
                      {...(!option.disabled && !isNullOrUndefined(option.href)
                        ? {
                            to: option.href,
                          }
                        : {})}
                    >
                      {() => (
                        <>
                          {option.icon &&
                            React.createElement(option.icon, {
                              className: twMerge(
                                "size-4 flex-shrink-0",
                                isOptionActive && "text-blue-dark",
                                option.disabled && "text-grey-hover",
                                option.iconClassName,
                              ),
                            })}

                          <span
                            className={twMerge(
                              shouldTruncateOptions && "truncate",
                            )}
                          >
                            {option.label as string}
                          </span>
                          {option.isError && (
                            <ExclamationCircleIcon className="absolute right-4 top-1/2 size-4 flex-shrink-0 -translate-y-1/2 text-red-dark" />
                          )}
                        </>
                      )}
                    </Menu.Item>
                  );
                })}
                {!options.length && (
                  <div className="flex h-10 items-center justify-center">
                    <h5>Pasirinkimų nėra.</h5>
                  </div>
                )}
              </Menu.Items>
            </FloatingPortal>
          </div>
        </div>
      )}
    </Menu>
  );
};
